import {
  tokens,
  Typography,
  Skeleton,
  Modal,
  useSingletonTabletMediaQuery,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import React, { useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import { useParams } from "react-router-dom";
import { useFlags } from "flagsmith/react";
import { Asset } from "../../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useCapContext } from "../../../providers/CapContext";
import { Column, Row } from "../../atoms/layoutAtoms";
import { FinancialTermsRows } from "../../molecules/FinancialTermRows";
import { SystemRows } from "../../molecules/SystemRows";
import { ProductRows } from "../../molecules/ProductRows";
import { ViewType } from "../../../capTypes";
import { HamburgerMenu, FryProps } from "../../molecules/HamburgerMenu";
import { CapSectionTitle } from "../../molecules/CapStepSection";
import { FullSizeCarousel } from "../../molecules/FullSizeCarousel";
import { ProductionModal } from "../../molecules/ProductionModal";
import { StyledCapPhotoModal } from "../HomeReadinessStepView";
import { CapNearbyCustomersSection } from "../CapNearbyCustomersSection";
import {
  capBatteryImageKeyFromManufacturer,
  ComparisonViewImage,
  SingleViewImage,
} from "./DesignStepViewCommon";
import { SimplifiedDesignStepContent } from "./SimplifiedDesignStepContent";
import { useNavigateHelper } from "utils/useNavigateHelper";
import { getReworkTaskByName } from "cap/api/capApi";
import { useAppSelector } from "store";
import { useFlag } from "hooks/useFlag";
import { FeatureFlags } from "fixtures/features";
import { isSignedProposal } from "cap/utils/proposalHelpers";
import { getCapPackageDoc } from "cap/constants/capPackageStatus";
import { setCapRepRedesignBaselineDocId } from "cap/utils/capRepRedesignDocRefresh";
import { CAP__NAVIGATED_TO_REP_REDESIGN_FROM_MAKE_MY_OWN_CHANGES_ON_DESIGN_STEP } from "constants/mixpanelEvents";
import { Mixpanel } from "providers/mixpanel";

const DesignStepView: React.FC = () => {
  const {
    activeProposal,
    activeProposalDesignImages,
    activeProposalLoading,
    cap,
    capTask,
    contacts,
    documents,
    isCapLoading,
    isContactsLoading,
    isCustomer,
    isSowLoading,
    isNonSolar,
    isOnlyBattery,
    isOnlyEvCharger,
    originalProposal,
    originalProposalDesignImages,
    originalProposalDesignImagesLoading,
    proposals,
    prospect,
    prospectLoading,
    sfdcUrl,
    sow,
    isCapSimplifiedExperience,
  } = useCapContext();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const navigate = useNavigateHelper({ replace: true });
  const repRedesignAtCapEnabled = useFlag(FeatureFlags.REP_REDESIGN_AT_CAP);
  const repRedesignPostLiveDate = useFlags([FeatureFlags.REP_REDESIGN_IS_LIVE])[
    FeatureFlags.REP_REDESIGN_IS_LIVE
  ]?.value;
  const { prospectId } = useParams();
  const [viewType, setViewType] = useState<ViewType>("Single");
  const [showFullSizeImage, setShowFullSizeImage] = useState(false);
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [completedTaskComplete, setCompletedTaskComplete] = useState(false);
  const [showUnsignedProposalModal, setShowUnsignedProposalModal] =
    useState(false);

  const showRepRedesignFlow =
    !!repRedesignPostLiveDate &&
    !!activeProposal?.createdDate &&
    repRedesignPostLiveDate < activeProposal.createdDate;

  const { ultimateParentProposal } = sow || {};

  const signedProposal = isSignedProposal(
    activeProposal,
    originalProposal,
    ultimateParentProposal
  );

  let smallOriginalImage, largerOriginalImage: { url: string };
  if (originalProposalDesignImages) {
    smallOriginalImage = originalProposalDesignImages.find(
      (image: { documentName: string | string[] }) =>
        image.documentName.includes("design-image-small.jpg")
    );
    largerOriginalImage = originalProposalDesignImages.find(
      (image: { documentName: string | string[] }) =>
        image.documentName.includes("design-image-large.jpg")
    );
  }

  let smallProposalImage, largerNewImage: { url: string };
  if (activeProposalDesignImages) {
    smallProposalImage = activeProposalDesignImages.find(
      (image: { documentName: string | string[] }) =>
        image.documentName.includes("design-image-small.jpg")
    );

    largerNewImage = activeProposalDesignImages.find(
      (image: { documentName: string | string[] }) =>
        image.documentName.includes("design-image-large.jpg")
    );
  }

  const { proposalOrigin, parentProposalId } = activeProposal || {};

  useEffect(() => {
    if (prospectId && authKey) {
      getReworkTaskByName(authKey, prospectId, "COMPLETE CAP PACKAGE").then(
        (data) => {
          const isComplete = data?.length
            ? data[0]?.status === "Complete"
            : false;

          setCompletedTaskComplete(isComplete);
        }
      );
    }
  }, [prospectId, authKey]);

  const canViewChangeOrder = proposalOrigin === "Offer Experience D2";

  useEffect(() => {
    if ((cap?.type === "B" || cap?.type === "D") && parentProposalId) {
      setViewType("Comparison");
    }
  }, [parentProposalId, cap?.type]);
  const openNewPage = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const capInSfdcUrl = `${sfdcUrl}/apex/wftask_lwc?id=${capTask?.id}&sfdc.override=1&c__tabName=DETAILS`;
  const specificationLinks: FryProps[] = [
    {
      action: () => openNewPage(capInSfdcUrl),
      label: "Request design change",
    },
    ...(repRedesignAtCapEnabled
      ? [
          {
            action: () => {
              Mixpanel.track(
                CAP__NAVIGATED_TO_REP_REDESIGN_FROM_MAKE_MY_OWN_CHANGES_ON_DESIGN_STEP,
                {
                  prospectId,
                  proposalId: activeProposal?.proposalId,
                }
              );
              const capPackageDoc = getCapPackageDoc(documents);
              if (capPackageDoc?.documentId && prospectId) {
                setCapRepRedesignBaselineDocId(
                  prospectId,
                  capPackageDoc.documentId
                );
              }
              navigate(`/rep-redesign`);
            },
            label: "Update design myself",
            disabled: !showRepRedesignFlow,
            tooltip: !showRepRedesignFlow
              ? "This feature is not available on this proposal"
              : undefined,
          },
        ]
      : []),
  ];
  const termsLinks: FryProps[] = [
    {
      action: () => {
        signedProposal
          ? navigate(`/rep-change-order`)
          : setShowUnsignedProposalModal(true);
      },
      label: "Edit pricing",
      disabled: completedTaskComplete,
      showTooltipWhileOpen: completedTaskComplete,
      tooltip: completedTaskComplete
        ? "Repricing is not available once CAP is closed. Please contact sales support to re-open CAP if you want to change price. Call Sales Support Desk at 888-840-7180 or text at 385-317-0193"
        : undefined,
    },
  ];

  const isLoading =
    !contacts ||
    isContactsLoading ||
    activeProposalLoading ||
    isSowLoading ||
    prospectLoading ||
    originalProposalDesignImagesLoading ||
    isCapLoading;

  const [fullSizeImageUrls, setFullSizeImageUrls] = useState<string[]>([]);

  const openLargeImageCarousel = (imageType: string) => {
    if (imageType === "original") {
      setFullSizeImageUrls(
        [largerOriginalImage?.url].filter(Boolean) as string[]
      );
    } else {
      setFullSizeImageUrls([largerNewImage?.url].filter(Boolean) as string[]);
    }
    setShowFullSizeImage(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const DesignRowHeader = ({
    title,
    links,
    menuLabel,
    testId = "",
  }: {
    title: string;
    links: FryProps[] | null;
    menuLabel: string;
    testId?: string;
  }) => {
    return (
      <HeaderRow isNonSolar={isNonSolar} data-testid={testId}>
        {isLoading ? (
          <HeaderContainerSkeleton />
        ) : (
          <>
            <Spacer />
            <HeaderContainer isMobile={isMobile}>{title}</HeaderContainer>
            {links ? (
              <HamburgerWrapper $isMobile={isMobile}>
                {!isCustomer && (
                  <HamburgerMenu
                    fries={links}
                    {...(!isMobile && { buttonLabel: menuLabel })}
                  />
                )}
              </HamburgerWrapper>
            ) : (
              <Spacer />
            )}
          </>
        )}
      </HeaderRow>
    );
  };

  const toggleProductionModal = () =>
    setShowProductionModal(!showProductionModal);
  let batteryManufacturer = isNonSolar
    ? prospect?.activeProposal?.assets.filter((asset: Asset) => {
        return (
          asset?.type === "Energy Storage System" ||
          asset?.type?.toLowerCase().includes("energy storage system")
        );
      })[0]?.manufacturer
    : undefined;

  if (isNonSolar && !batteryManufacturer && proposals?.length) {
    const proposal =
      proposals.find((p) => p.proposalId === activeProposal?.proposalId) ??
      proposals.find((p) => p.id === sow?.lastCustomerSignedProposalId) ??
      proposals.find((p) => p.signed);

    batteryManufacturer = proposal?.assetInfo?.filter((asset: Asset) => {
      return (
        asset?.type === "Energy Storage System" ||
        asset?.type?.toLowerCase().includes("energy storage system")
      );
    })[0]?.manufacturer;
  }

  const batteryImageKey = isNonSolar
    ? capBatteryImageKeyFromManufacturer(batteryManufacturer)
    : undefined;

  const getSingleViewHeaderText = () => {
    if (isOnlyBattery) {
      return "Review your battery specifications";
    }
    if (isOnlyEvCharger) {
      return "Review your EV charger specifications";
    }
    return "Review your finalized design";
  };

  return (
    <>
      {showUnsignedProposalModal && (
        <Modal
          testId="unsigned-proposal-modal"
          title="The CAP parent proposal is not signed."
          onClose={() => setShowUnsignedProposalModal(false)}
        >
          <Typography>
            A new parent proposal was generated for CAP, repricing is only
            available when there is a signed parent proposal. This is not a
            glitch or bug.
          </Typography>
        </Modal>
      )}
      {!isCapSimplifiedExperience && (
        <CapSectionTitle>
          {isLoading ? <Skeleton /> : getSingleViewHeaderText()}
        </CapSectionTitle>
      )}

      {showProductionModal && (
        <ProductionModal
          proposal={activeProposal}
          toggleModal={toggleProductionModal}
        />
      )}

      {isCapSimplifiedExperience && (
        <SimplifiedDesignStepContent
          viewType={viewType}
          isLoading={isLoading}
          smallOriginalImage={smallOriginalImage}
          smallProposalImage={smallProposalImage}
          batteryImageKey={batteryImageKey}
          showFullSizeImage={showFullSizeImage}
          openLargeImageCarousel={openLargeImageCarousel}
          specificationLinks={specificationLinks}
          termsLinks={canViewChangeOrder ? termsLinks : null}
          toggleProductionModal={toggleProductionModal}
          DesignRowHeader={DesignRowHeader}
        />
      )}

      {/*Single view*/}
      {viewType === "Single" && !isCapSimplifiedExperience && (
        <DesignCard
          data-testid="design-step-view-card-single"
          isMobile={isMobile}
          isNonSolar={isNonSolar}
          isTablet={isTablet}
        >
          {isLoading ? (
            <DesignImageSkeleton isNonSolar={isNonSolar} />
          ) : (
            <SingleViewImage
              isOnlyBattery={isOnlyBattery}
              isOnlyEvCharger={isOnlyEvCharger}
              batteryImageKey={batteryImageKey}
              showFullSizeImage={showFullSizeImage}
              onOpenLargeImageCarousel={() => openLargeImageCarousel("new")}
              smallProposalImage={smallProposalImage}
              testId="offer-experience--DesignStepView--image-single"
            />
          )}
          <DesignTableContainer>
            <DesignRowHeader
              title="Specifications"
              links={specificationLinks}
              menuLabel="Update design"
            />
            <SystemRows
              viewType={viewType}
              isLoading={isLoading}
              toggleProductionModal={toggleProductionModal}
            />
            <ProductRows viewType={viewType} isLoading={isLoading} />
            <DesignRowHeader
              title="Terms"
              links={canViewChangeOrder ? termsLinks : null}
              menuLabel="Edit"
              testId="design-terms-header"
            />
            <FinancialTermsRows viewType={viewType} isLoading={isLoading} />
          </DesignTableContainer>
        </DesignCard>
      )}
      {showFullSizeImage && (
        <StyledCapPhotoModal
          isTablet={isTablet}
          isMobile={isMobile}
          testId="design-step-view-modal"
          onClose={() => setShowFullSizeImage(false)}
        >
          <FullSizeCarousel
            requestedIndex={0}
            imageUrls={fullSizeImageUrls}
            options={{ inViewThreshold: 1 }}
            showDots={true}
          />
        </StyledCapPhotoModal>
      )}
      {/*Comparison view*/}
      {viewType === "Comparison" && !isCapSimplifiedExperience && (
        <DesignCard
          data-testid="design-step-view-card-comparison"
          isMobile={isMobile}
          isNonSolar={false}
          isTablet={isTablet}
        >
          <DesignImageRow isMobile={isMobile}>
            <DesignImageColumn>
              {isLoading ? (
                <DesignImageSkeleton isNonSolar={false} />
              ) : (
                <ComparisonViewImage
                  title="Original"
                  isOnlyBattery={isOnlyBattery}
                  isOnlyEvCharger={isOnlyEvCharger}
                  batteryImageKey={batteryImageKey}
                  img={smallOriginalImage}
                  onOpenLargeImageCarousel={() =>
                    openLargeImageCarousel("original")
                  }
                  showFullSizeImage={showFullSizeImage}
                  testId="offer-experience--DesignStepView--image-original"
                />
              )}
            </DesignImageColumn>
            <DesignImageColumn>
              {isLoading ? (
                <DesignImageSkeleton isNonSolar={false} />
              ) : (
                <ComparisonViewImage
                  title="Final Design"
                  isOnlyBattery={isOnlyBattery}
                  isOnlyEvCharger={isOnlyEvCharger}
                  batteryImageKey={batteryImageKey}
                  img={smallProposalImage}
                  onOpenLargeImageCarousel={() => openLargeImageCarousel("new")}
                  showFullSizeImage={showFullSizeImage}
                  testId="offer-experience--DesignStepView--image-final"
                />
              )}
            </DesignImageColumn>
          </DesignImageRow>
          <DesignRowHeader
            title="Specifications"
            links={specificationLinks}
            menuLabel="Update design"
          />
          <SystemRows
            viewType={viewType}
            isLoading={isLoading}
            toggleProductionModal={toggleProductionModal}
          />
          <ProductRows viewType={viewType} isLoading={isLoading} />
          <DesignRowHeader
            title="Terms"
            links={canViewChangeOrder ? termsLinks : null}
            menuLabel="Edit"
            testId="design-terms-header"
          />
          <FinancialTermsRows viewType={viewType} isLoading={isLoading} />
        </DesignCard>
      )}
      <CapNearbyCustomersSection />
    </>
  );
};

const DesignImageRow = styled(Row)`
  flex-direction: row;
  gap: ${({ isMobile }) => (isMobile ? "12px" : "60px")};
  padding: 0;
`;

const DesignImageColumn = styled(Column)`
  flex-grow: 1;
  align-items: center;
  width: 50%;

  & > p {
    padding: 8px;
    margin-bottom: 16px;
  }
`;

const HamburgerWrapper = styled.div<{ $isMobile: boolean }>`
  padding-right: 24px;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 32px;
    height: 32px;
  `
      : `
    display: flex;
    align-items: center;
    overflow: visible;
  `}
`;

const Spacer = styled.div`
  width: 56px;
  /* the same width as the hamburger icon wrapper, 32px, plus it's padding, 24px */
`;

const DesignCard = styled.section<{
  isMobile: boolean;
  isTablet: boolean;
  isNonSolar: boolean;
}>`
  display: flex;
  flex-direction: ${({ isNonSolar, isMobile }) =>
    isNonSolar && !isMobile ? "row" : "column"};
  justify-content: space-evenly;
  background: ${tokens.BACKGROUND_LIGHT};
  border-radius: 8px;
  padding: ${({ isTablet, isMobile }) =>
    isMobile ? "16px 16px 0px" : isTablet ? "48px 24px" : "32px 64px 0px"};
`;

const DesignImageSkeleton = styled(Skeleton)<{ isNonSolar: boolean }>`
  height: 443px;
  width: 100%;
  margin-bottom: 32px;
  margin-right: ${({ isNonSolar }) => (isNonSolar ? "64px" : "0px")};
`;

const DesignTableContainer = styled.span`
  width: 100%;
`;

const HeaderRow = styled(Row)<{ isNonSolar: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: ${({ isNonSolar }) => (isNonSolar ? "unset" : "100%")};
  background: #f4f1ec;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px rgba(208, 204, 195, 0.6) solid;
  padding: 0;
`;

const HeaderContainer = styled.div<{ isMobile: boolean }>`
  color: #1f2647;
  line-height: ${({ isMobile }) => (isMobile ? "19px" : "22px")};
  font-size: ${({ isMobile }) => (isMobile ? "16px" : "19px")};
  font-family: "Roobert", sans-serif;
  font-weight: 500;
  word-wrap: break-word;
  padding: 24px;
`;

const HeaderContainerSkeleton = styled(Skeleton)`
  padding: 24px;
  flex: 1 1 0;
  text-align: center;
  color: #1f2647;
  font-size: 19px;
  font-family: "Roobert", sans-serif;
  font-weight: 500;
  word-wrap: break-word;
`;

export { DesignStepView };
